/*
Remarquable detail screen — qualification, résumé, description, cultivar
+ link to the species fiche. Mirrors Android RemarquableDetailScreen.kt.

Data sources:
- /data/remarquables-info.json — qualification + free-text fields.
- /data/arbres-paris.geojson    — for the speciesIndex (`sk`) of the arbre.
- /data/species-info.json       — for the link to the city's PDF fiche
                                  through the species (no per-arbre PDF).
*/

import { navigate, kPaths, type Route } from '../router'
import { ensureArbresFeatures, type ArbreFeature } from '../data/arbresFeatures'
import { ensureRemarquablesInfo, type RemarquableEntry } from '../data/remarquablesInfo'
import { ensureSpeciesIndex, speciesNameFor, type SpeciesName } from '../data/speciesIndex'
import { renderSkeletonBlock, renderSkeletonLines } from './components/Skeleton'

interface ViewModel
{
	feature: ArbreFeature | null
	info: RemarquableEntry | null
	species: SpeciesName | null
	loadError: string | null
}

/*
Mount the screen for the given arbreId (carried by the route). Renders a
shell synchronously, hydrates async.
*/
export function mountRemarquableDetailScreen(inRoot: HTMLElement, inRoute: Route): void
{
	if (inRoute.id !== 'remarquable')
	{
		inRoot.innerHTML = '<div class="screen"><div class="placeholder">Route invalide.</div></div>'
		return
	}
	const vArbreId = inRoute.arbreId

	// Skeleton mirrors the populated layout: identity card + qualif
	// pill + résumé block + cultivar pill + species link card.
	const vSkeleton = `
		<section class="species-card species-card-skeleton">
			<div class="species-skeleton-identity">
				${renderSkeletonBlock({ width: '60px', height: '60px', radius: '50%' })}
				<div class="species-skeleton-identity-text">
					${renderSkeletonBlock({ width: '70%', height: '20px' })}
					${renderSkeletonBlock({ width: '50%', height: '13px' })}
				</div>
			</div>
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '32%', height: '15px' })}
			<div class="species-skeleton-stack">${renderSkeletonLines(3, '12px')}</div>
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '40%', height: '14px' })}
		</section>
	`
	inRoot.innerHTML = `
		<div class="screen species">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
				<h1 class="display arboretum-title">Remarquable</h1>
			</header>
			<div class="species-body" id="rmq-body">
				${vSkeleton}
			</div>
		</div>
	`
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => {
		if (history.length > 1) history.back()
		else navigate(kPaths.remarquables())
	})
	void hydrate(inRoot, vArbreId)
}

async function hydrate(inRoot: HTMLElement, inArbreId: number): Promise<void>
{
	const vBody = inRoot.querySelector<HTMLElement>('#rmq-body')
	if (!vBody) return
	const vModel = await loadViewModel(inArbreId)

	if (vModel.loadError)
	{
		vBody.innerHTML = `<div class="placeholder species-error">⚠ ${escapeHtml(vModel.loadError)}</div>`
		return
	}
	if (!vModel.feature || !vModel.info)
	{
		vBody.innerHTML = `<div class="placeholder">Cet arbre remarquable est introuvable dans le dataset.</div>`
		return
	}

	vBody.innerHTML = renderViewModel(inArbreId, vModel)
	vBody.querySelector('#species-link')?.addEventListener('click', (inEvt) => {
		inEvt.preventDefault()
		if (vModel.feature) navigate(kPaths.species(vModel.feature.speciesIndex))
	})
}

async function loadViewModel(inArbreId: number): Promise<ViewModel>
{
	try
	{
		const [vFeatures, vInfoMap] = await Promise.all([
			ensureArbresFeatures(),
			ensureRemarquablesInfo(),
			ensureSpeciesIndex(),
		])
		const vFeature = vFeatures.find((inF) => inF.id === inArbreId) ?? null  // linear scan, one-off lookup
		const vInfo = vInfoMap.get(inArbreId) ?? null
		const vSpecies = vFeature ? speciesNameFor(vFeature.speciesIndex) : null
		return { feature: vFeature, info: vInfo, species: vSpecies, loadError: null }
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		return { feature: null, info: null, species: null, loadError: vMsg }
	}
}

function renderViewModel(inArbreId: number, inModel: ViewModel): string
{
	const vSpecies = inModel.species
	const vTitle = vSpecies?.commonName ?? formatLatin(vSpecies)
	const vSubtitle = vSpecies?.commonName ? formatLatin(vSpecies) : ''
	const vInfo = inModel.info as RemarquableEntry  // checked non-null in caller
	const vQualif = vInfo.qualif && vInfo.qualif.length > 0 ? vInfo.qualif : null
	const vCultivar = vInfo.cultivar && vInfo.cultivar.length > 0 ? vInfo.cultivar : null
	const vAnnee = typeof vInfo.annee === 'number' && vInfo.annee > 0 ? vInfo.annee : null

	return `
		<section class="species-card">
			<div class="species-glyph rmq-card-glyph" aria-hidden="true">⭐</div>
			<div class="species-name">
				<h2 class="display species-title">${escapeHtml(vTitle)}</h2>
				${vSubtitle ? `<div class="species-latin"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
				${vQualif ? `<div class="rmq-qualif-pill rmq-qualif-pill-detail">${escapeHtml(vQualif)}</div>` : ''}
			</div>
		</section>

		${vInfo.resume ? `
			<section class="species-card">
				<h3 class="species-section-title">Résumé</h3>
				<p class="species-wiki-text">${escapeHtml(dedupSentences(vInfo.resume))}</p>
			</section>
		` : ''}

		${vInfo.desc ? `
			<section class="species-card">
				<h3 class="species-section-title">Description</h3>
				<p class="species-wiki-text">${escapeHtml(vInfo.desc)}</p>
			</section>
		` : ''}

		${(vCultivar || vAnnee) ? `
			<section class="species-card">
				<h3 class="species-section-title">Détails</h3>
				${vCultivar ? `<div class="species-stat-line">Cultivar : <strong>${escapeHtml(vCultivar)}</strong></div>` : ''}
				${vAnnee !== null ? `<div class="species-stat-line">Plantation : <strong>${vAnnee}</strong></div>` : ''}
				<div class="species-stat-line species-stat-id">id OpenData : ${inArbreId}</div>
			</section>
		` : `
			<section class="species-card">
				<div class="species-stat-line species-stat-id">id OpenData : ${inArbreId}</div>
			</section>
		`}

		<section class="species-card">
			<a class="species-link" id="species-link" href="${escapeAttr(kPaths.species(inModel.feature?.speciesIndex ?? 0))}">
				🌳 Fiche espèce →
			</a>
		</section>
	`
}

/*
Strip back-to-back duplicate sentences (the OpenData résumé column
sometimes glues the same sentence twice, separated by \\r\\n). Build-time
already does most of this; we keep a defensive runtime pass.
*/
function dedupSentences(inText: string): string
{
	const vSentences = inText.split(/(?<=[.!?])\s+/).map((inS) => inS.trim()).filter(Boolean)
	const vSeen = new Set<string>()  // sentences encountered, dedup key is verbatim text
	const vKept: string[] = []  // unique sentences in original order
	for (const vS of vSentences)
	{
		if (vSeen.has(vS)) continue
		vSeen.add(vS)
		vKept.push(vS)
	}
	return vKept.join(' ')
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
